'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';

import { useContext, useEffect, useState } from "react";
import TextInput from "../common/inputs/text_input";
import "./ideas.css";
import classes from "./ideas.module.css";
import Search from '@/assets/svg/icons/search.svg';
import X from '@/assets/svg/icons/x.svg';
import ChevronRight from '@/assets/svg/icons/chevron-right.svg';
import ChevronLeft from '@/assets/svg/icons/chevron-left.svg';
import AlertCircle from '@/assets/svg/icons/alert-circle.svg';
import CustomSVG from '../common/svg';
import IdeasInfoContext from '@/providers/ideas-info-context';
import Link from 'next/link';
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';
import SafeImage from '@/components/common/safe_image';
import ArrowUpRight from '@/assets/svg/icons/arrow-up-right.svg';
import ArrowDown from '@/assets/svg/icons/arrow-down.svg';
import CustomButton from '../common/buttons/button';
import { useSanitizedHTML } from '@/utils/sanitizeHTMLHooks';
import { withTippekNewsFilter } from '@/utils/newsFilters';

const IDEAS_PROVIDER_CARD_SIZES = '(max-width: 768px) 100vw, 400px';
const IDEAS_NEWS_CARD_SIZES = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px';

async function triggerSlideHaptic() {
    try {
        const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
        await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
        // no-op on web / unsupported platforms
    }
}
import { useMutation } from '@tanstack/react-query';
import { getNewsCategory, getNewsLatest } from '@/utils/http';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

const thirty_percent_index = 3;

export default function Ideas({url, news, pager, searchInit, title}) {
    const router = useRouter();
    const path = usePathname();
    const search_params = useSearchParams();
    const ideasInfo = useContext(IdeasInfoContext);

    const [search, setSearch] = useState(searchInit ? searchInit.substring(1, searchInit.length-1) : "");
    const [newsClient, setNewsClient] = useState(news);
    const [pagerClient, setPagerClient] = useState(pager);
    const [mounted, setMounted] = useState(false); //hyratation error handling on dangerouslySetInnerHTML
    const [isPopState, setIsPopState] = useState(false);
    const shouldExcludeWeddingHallNews = path === "/tippek-otletek" || path === "/eskuvoi-tippek-otletek" || path.startsWith("/eskuvoi-tippek-otletek/");
    
    const params = new URLSearchParams(Array.from(search_params.entries()));

    // Determine page title based on URL or prop
    const pageTitle = title || (url === "hazassagkoto-terem" ? "Házasságkötő termek" : "Esküvői tippek és ötletek");

    useEffect(() => {setNewsClient(news)}, [news]);
    useEffect(() => {setPagerClient(pager)}, [pager]);

    useEffect(() => {
        if (isPopState) {
            setSearch(search_params.get("search") ? search_params.get("search").substring(1, search_params.get("search").length-1) : "")
            setIsPopState(false);
        }
    }, [search_params]);

    useEffect(() => {
        if(!mounted) setMounted(true);
        const handlePopState = () => {
            setIsPopState(true);
        };
    
        window.addEventListener('popstate', handlePopState);
    
        return () => {
          window.removeEventListener('popstate', handlePopState);
        };
    }, [])

    const handleInputChange = (val) => {
        setSearch(val);
    }
    
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            const params = new URLSearchParams(Array.from(search_params.entries()));
            if(search.trim()) {
                params.set("search", `*${search}*`);
            } else {
                params.delete("search");
            }
            router.push(`${path}?${params.toString()}`, {scroll: false});
        }, 1000)
      
          return () => clearTimeout(delayDebounceFn)
    }, [search])
    
    const { mutate: getMoreLatest } = useMutation({
        mutationFn: getNewsLatest,
        onSuccess: (response, params) => {
            if(response.success) {
                setNewsClient(prev => {
                    return [...prev, ...response.response.news]
                })
                setPagerClient(response.pager);
            }
        }
    });
    
    const { mutate: getMoreCategory } = useMutation({
        mutationFn: getNewsCategory,
        onSuccess: (response, params) => {
            if(response.success) {
                setNewsClient(prev => {
                    return [...prev, ...response.response.news]
                })
                setPagerClient(response.pager);
            }
        }
    });
    
    const showMore = () => {
        let temp_search = {};
        if(search.trim()) temp_search["search"] = `*${search}*`;

        if(url === "") {
            const latestParams = {page: pagerClient.page+1, per_page: pagerClient.per_page, ...temp_search};
            getMoreLatest({params: shouldExcludeWeddingHallNews ? withTippekNewsFilter(latestParams) : latestParams});
        }
        else {
            const categoryParams = {page: pagerClient.page+1, per_page: pagerClient.per_page, url: path.slice(1), ...temp_search};
            getMoreCategory({id: 0, params: shouldExcludeWeddingHallNews ? withTippekNewsFilter(categoryParams) : categoryParams});
        }
    }

    const providerClickHandler = (url) => {
        router.push(`/${url}`)
    }

    let startingSlide = 0;
    if(url !== "") {
        let temp_index = ideasInfo.categories.findIndex(c => c.url === url);
        if(temp_index !== -1) startingSlide = temp_index;
    }

    function NoResultDiv() {
        return <div className={`${classes["ideas-no-result-container"]}`}>
            <CustomSVG className="flex-shrink-0 stroke-warning-500 circle-stroke-warning-500" SvgFunction={AlertCircle} height={24} width={24} />
            <div className="d-flex flex-grow-1 flex-column align-self-stretch align-items-start gap-3">
                <div className="d-flex w-100 flex-column align-self-stretch align-items-start gap-1">
                    <p className="margin-0 color-gray-800 text-sm-semibold">Nincs találat</p>
                    <p className="margin-0 color-gray-600 text-sm-regular">A megadott keresésnek megfelelően nincs találat, módosítsd vagy töröld a keresést.</p>
                </div>
            </div>
        </div>
    }

    return <div className={`${classes["ideas-main-container"]}`}>
        <h1 className="margin-0 color-gray-700 display-sm-bold text-center">{pageTitle}</h1>
        <div>
            <TextInput className={`${classes["ideas-search"]}`} type="text" placeholder="Keresés" leading_icon={Search} value={search} onChange={e => handleInputChange(e.target.value)} help_icon={search ? X : undefined} help_icon_click={() => setSearch("")} />
        </div>
        <Swiper
            slidesPerView={"auto"}
            spaceBetween={0}
            freeMode={true}
            navigation={{
              nextEl: `.ideas-category-nav-right`,
              prevEl: `.ideas-category-nav-left`,
              disabledClass: "swiper-button-disabled"
            }}
            initialSlide={startingSlide}
            modules={[FreeMode, Navigation]}
            onSlideChange={triggerSlideHaptic}
            className={`${classes["ideas-category-swiper"]}`}
        >
            <SwiperSlide key={`ideas-category-item-all`} className={`${classes["swiper-item"]}`} onClick={() => {}} > 
                <Link className={`${classes["ideas-category-swiper-item"]}`} title={url === "hazassagkoto-terem" ? "Házasságkötő termek" : "Esküvői tippek és ötletek"} href={url === "hazassagkoto-terem" ? `/hazassagkoto-terem?${params.toString()}` : `/tippek-otletek?${params.toString()}`} >
                    <p className={`${classes["ideas-category-swiper-item-inner"]} text-lg-medium ${"" === url ? classes["active"] : ""}`}>
                        Összes
                    </p>
                </Link>
            </SwiperSlide>
            {ideasInfo && ideasInfo.categories && ideasInfo.categories.map(d => <SwiperSlide key={`ideas-category-item-${d.id}`} className={`${classes["swiper-item"]}`} onClick={() => {}} > 
                <Link className={`${classes["ideas-category-swiper-item"]}`} title={d.name} href={`/${d.url}?${params.toString()}`} >
                    <p className={`${classes["ideas-category-swiper-item-inner"]} text-lg-medium ${`/${d.url}` === path ? classes["active"] : ""}`}>
                        {d.name}
                    </p>
                </Link>
            </SwiperSlide>)}
            <div className={`ideas-category-nav-left`}>
                <div className='position-relative'>
                    <div className={`ideas-category-nav`}>
                        <CustomSVG className="stroke-gray-700" SvgFunction={ChevronLeft} height={20} width={20} />
                    </div>
                    <div className={`ideas-category-nav-left-hide`}></div>
                </div>
            </div>
            <div className={`ideas-category-nav-right`}>
                <div className='position-relative'>
                    <div className={`ideas-category-nav`}>
                        <CustomSVG className="stroke-gray-700" SvgFunction={ChevronRight} height={20} width={20} />
                    </div>
                    <div className={`ideas-category-nav-right-hide`}></div>
                </div>
            </div>
        </Swiper>
        <div className={`${classes["news-container-main"]}`}>
            <div className={`${classes["news-container-news-section"]}`}>
                {newsClient && newsClient.length > 0 && newsClient.map((n, i) => 
                    <SafeIdeaCard key={`idea-card-${n.id}`} idea={n} index={i} thirty_percent_index={thirty_percent_index} mounted={mounted} />
                )}
                {pagerClient.page !== pagerClient.page_nums && pagerClient.page_nums > 0 && <div className="d-flex flex-column align-self-stretch align-items-center w-100">
                    <CustomButton size="lg" hierarchy="secondary-gray" icon={"leading"} is_svg={true} svg={ArrowDown} onClick={showMore} >Továbbiak</CustomButton>
                </div>}
                {newsClient && newsClient.length === 0 &&
                    <NoResultDiv />
                }
            </div>
            <div className={`${classes["news-container-popular-section"]}`}>
                {ideasInfo.news && ideasInfo.news.length > 0 && <div className={`${classes["popular-section-news"]}`}>
                    <div className={`${classes["popular-section-news-header"]}`}>
                        <p className="margin-0 color-gray-900 text-md-medium">Legolvasottabb Esküvői tippek, ötletek</p>
                    </div>
                    {ideasInfo.news.map(n => <Link key={`popular-news-item-${n.id}`} href={`/${n.url}`} title={n.name} className={`${classes["popular-section-news-item"]}`}>
                        <p className={`${classes["popular-section-news-item-text"]} color-gray-900 text-sm-medium`}>{n.name}</p>
                    </Link>)}
                </div>}
                {ideasInfo.providers && ideasInfo.providers.length > 0 && <div className={`${classes["popular-section-providers"]}`}>
                    <div className={`${classes["popular-section-providers-header"]}`}>
                        <p className="margin-0 color-gray-900 text-md-medium">Kiemelt szolgáltatóink</p>
                    </div>
                    {ideasInfo.providers.map(p => <div key={`popular-providers-item-${p.id}`} className={`${classes["popular-section-providers-item"]}`} onClick={() => providerClickHandler(p.url)}>
                        {p.images && p.images.length > 0
                            ? <SafeImage src={p.images[0]} fill sizes={IDEAS_PROVIDER_CARD_SIZES} quality={90} alt={p.name} style={{ objectFit: 'cover' }} />
                            : <SafeImage sizes={IDEAS_PROVIDER_CARD_SIZES} src={EmptyProviderLogo} alt={"Szolgáltató képe"} />
                        }
                        <div className={`${classes["popular-section-providers-item-footer"]}`}>
                            <p className={`${classes["popular-section-providers-item-text"]} color-gray-900 text-sm-medium`}>{p.name}</p>
                        </div>
                    </div>)}
                </div>}
            </div>
        </div>
    </div>
}

function SafeIdeaCard({ idea: n, index: i, thirty_percent_index, mounted }) {
    const sanitizedDescription = useSanitizedHTML(n.short_description || '');
    
    return (
        <Link 
            key={`new-section-client-item-${n.id}`} 
            href={`/${n.url}`} 
            title={n.name} 
            className={`${classes["news-section-card-main"]} pointer text-decoration-none`} 
            style={{flexBasis: "40%", maxWidth: (((i%8 === thirty_percent_index) && n) ? "40%" : "")}}
        >
            <div className={`${classes["news-section-client-card-img"]}`}>
                {n.image
                    ? <SafeImage src={n.image} fill sizes={IDEAS_NEWS_CARD_SIZES} quality={90} alt={n.name} style={{ objectFit: 'cover' }} />
                    : <SafeImage sizes={IDEAS_NEWS_CARD_SIZES} src={EmptyProviderLogo} alt={"Hirdetés képe"} />
                }
            </div>
            <div className={`${classes["news-section-client-card-text-container"]}`}>
                <div className={`${classes["news-section-client-card-badge"]} text-sm-medium`}>
                    {(n.category && n.category.name) ? n.category.name : "-"}
                </div>
                <div className="d-flex flex-row align-items-start gap-2 w-100">
                    <h3 className={`${classes["news-section-client-card-title"]} color-gray-800 text-lg-semibold`}>{n.name}</h3>
                    <CustomSVG className={`stroke-gray-800 flex-shrink-0 pointer`} SvgFunction={ArrowUpRight} height={24} width={24} />
                </div>
                {mounted && n.short_description && <p className={`${classes["news-section-client-card-description"]} color-gray-500 text-md-regular`} suppressHydrationWarning dangerouslySetInnerHTML={{__html: sanitizedDescription}} />}
            </div>    
        </Link>
    );
}